import { useLocation, useParams, useRouterState } from "@tanstack/react-router";
import type { JobType } from "api-types/jobs/jobTypes";
import type { LocationLevelType } from "api-types/locations/locationLevelTypes";
import type { FeatureName } from "common/constants/features";
import { CONTACT_EMAIL_ADDRESS, PRESS_EMAIL_ADDRESS } from "common/constants/marketing";
import type { UserNotificationType } from "common/types/userNotification";
import { objectToQueryParams } from "common/utils/fetchHelpers";
import { snakeCaseToCamelCase } from "common/utils/strings";
import { useMemo } from "react";
import type { PublicPlanType } from "./requestTypes";

// admin

export type AdminRoutes =
  | "builders"
  | "homes"
  | "users"
  | "features"
  | "settings"
  | "debug"
  | "plan-analysis"
  | "selections"
  | "component-library"
  | "integrations"
  | "data-migration";

export function adminPath({ path = "builders", builderId }: { path?: AdminRoutes; builderId?: string } = {}) {
  return `/admin/${path}${builderId ? `?builderId=${builderId}` : ""}`;
}

interface DebugPage {
  page: string;
  description?: string;
}

/**
 * Debug page directory (please keep in alphabetical order!)
 *
 * Each item in the array contains a "page" string that maps to the page's URL path; for example,
 * the string "props" maps to higharc.com/debug/props
 */
export const DEBUG_PAGES: DebugPage[] = [
  {
    page: "annotation",
    description: "Renders a variety of plan set annotations for visual debugging",
  },
  {
    page: "builder-website",
    description: "Test scripts for integrating widgets into builder websites (Showroom Intro)",
  },
  {
    page: "builder-website/sales-map",
  },
  {
    page: "builder-website/showroom-intro",
  },
  {
    page: "building-debugger",
    description: "Upload a building to interact with it, download assets, and toggle options",
  },
  {
    page: "building-gltf",
  },
  {
    page: "building-gltf-lambda",
  },
  {
    page: "building-renderer",
  },
  {
    page: "calibration-texture-test",
  },
  {
    page: "compare-roof-viewer",
  },
  {
    page: "crash-test",
  },
  {
    page: "design-material",
  },
  {
    page: "design-material-docs",
    description: "Documentation for our design material system",
  },
  {
    page: "design-material-editor",
    description: "Editor for design materials (realtime 3D viewer materials)",
  },
  {
    page: "error-403",
    description: "Verifies 403 redirects work correctly (used by E2E)",
  },
  {
    page: "export",
  },
  {
    page: "exr-test",
  },
  {
    page: "exterior-scene",
  },
  {
    page: "face-diff-viewer",
    description: "Visual face-diff comparison tool",
  },
  {
    page: "floor-framing",
    description: "Interactive floor framing test: draw perimeter, openings, constraint lines, view 3D framing",
  },
  {
    page: "wall-framing",
    description: "Interactive wall framing test: load vertical quad test cases, view 3D framing",
  },
  {
    page: "framing/truss2D",
  },
  {
    page: "geom",
  },
  {
    page: "geom/straight-skeleton-offset",
  },
  {
    page: "geom/straight-skeleton-offset-single",
  },
  {
    page: "geom/miter-skeleton",
    description: "Interactive mitered-wavefront straight-skeleton debugger with per-side widths and wedge tags",
  },
  {
    page: "gfx-stress/geometry-stress-incremental",
    description: "Manual incremental geometry loading stress test for testing platform limits",
  },
  {
    page: "gfx-stress/material-stress",
    description: "Automated material loading stress test",
  },
  {
    page: "gfx-stress/material-stress-incremental",
    description: "Manual incremental material loading stress test for testing platform limits",
  },
  {
    page: "gfx-test/material-render",
  },
  {
    page: "gfx-test/threeviewer-init",
  },
  {
    page: "hatch-material-generation",
    description: "Test the performance of autogenerated hatch patterns",
  },
  {
    page: "hatching-test",
    description: "Test for SDF-based hatch material WebGL rendering",
  },
  {
    page: "iframe-test",
    description: "Test how Higharc looks and behaves in an embedded iframe context",
  },
  {
    page: "image-service",
    description: "Test the output of the image optimization service",
  },
  {
    page: "material-appearance-test",
    description: "Test appearance or loading performance of multiple materials with different settings",
  },
  {
    page: "material-cache-manager",
  },
  {
    page: "material-id",
    description: "Helper for MaterialSrc objects",
  },
  {
    page: "material-thumbnails",
  },
  {
    page: "option-catalog-repair",
    description: "Detect and repair flip-flop damage in option catalog version history",
  },
  {
    page: "option-migration",
    description: "Migrate options from published studio docs to options catalog",
  },
  {
    page: "option-migration/migrate-options",
  },
  {
    page: "palette-thumbnail-cache-bust",
    description: "Compare existing cached palette thumbnails with freshly generated ones for debugging cache issues",
  },
  {
    page: "palette-thumbnail-test",
  },
  {
    page: "panorama-viewer",
    description: "View single equirectangular 360 images",
  },
  {
    page: "pdf-diff",
  },
  {
    page: "plan-render",
    description: "Render a plan with different settings for testing",
  },
  {
    page: "planar-graph",
  },
  {
    page: "polyclip/brep-set-ops-test",
  },
  {
    page: "polyclip/brep-trim-test",
  },
  {
    page: "polyclip/offset-closed-test",
  },
  {
    page: "polyclip/plane-trim-test",
  },
  {
    page: "polyclip/set-ops-bench",
  },
  {
    page: "polyclip/set-ops-fuzz",
  },
  {
    page: "polyclip/set-ops-test",
  },
  {
    page: "polyclip/snap-round-test",
  },
  {
    page: "polyclip/trim-faces-test",
  },
  {
    page: "props",
    description: "Browse built-in prop library in 3D",
  },
  {
    page: "render",
  },
  {
    page: "repair-home",
  },
  {
    page: "schema/building-api",
  },
  {
    page: "selections",
  },
  {
    page: "server-errors",
    description: "Test out different error codes",
  },
  {
    page: "showroom-floorplan",
    description: "Render the showroom floor plan viewer in isolation for visual debugging",
  },
  {
    page: "source-graph-analyzer",
    description: "Interpret source graph data",
  },
  {
    page: "ssrr-test",
  },
  {
    page: "stroke-test",
  },
  {
    page: "svg-bb",
  },
  {
    page: "text-test",
  },
  {
    page: "tonemapping-test",
    description: "Test how different colors appear with HDR tonemapping",
  },
  {
    page: "viewer-state-management",
  },
  {
    page: "walkthrough",
  },
  {
    page: "walkthrough-caching",
    description: "Controls for manual client-side caching of walkthrough data intended for demos",
  },
  {
    page: "wasm",
  },
  {
    page: "webgl-context-sharing",
    description: "Test case for WebGL multi-viewport functionality",
  },
  {
    page: "webgl-context-sharing-transform",
  },
  {
    page: "webgl-service",
  },
];

function idWithOptionalBuilderIdParams(
  id?: string,
  builderId?: string,
  otherParams?: { [key: string]: string },
): string {
  const params = new URLSearchParams();

  if (id) {
    params.set("id", id);
  }

  if (builderId) {
    params.set("builderId", builderId);
  }

  if (otherParams) {
    for (const key in otherParams) {
      params.set(key, otherParams[key]);
    }
  }

  const qp = params.toString();

  return qp ? `?${qp}` : "";
}

// marketing pages

export const landingPagesPath = "https://higharc.com";
export const solutionsPath = "https://solutions.higharc.com";
export const blogPath = "https://blog.higharc.com";

export const contactFormPath = `${landingPagesPath}/contact/get-in-touch`;
export const careersPath = `${landingPagesPath}/careers`;
export const aboutPath = `${landingPagesPath}/about`;

// legal pages

export const termsAndConditionsPath = `${landingPagesPath}/legal/terms-and-conditions`;
export const swLicensesPath = `${landingPagesPath}/legal/higharc-licenses`;
export const privacyPolicyPath = `${landingPagesPath}/legal/privacy-policy`;
export const termsOfServicePath = "/static/legal/higharc_tos.pdf";

// unauthenticated pages

export const homePath = "/account/login"; // if logged in, you'll be redirected to your 'home' page

export const graphicsTroubleshootingTestPath = "/graphics-troubleshooting-test";

// email links

export const contactPath = `mailto:${CONTACT_EMAIL_ADDRESS}`;
export const pressPath = `mailto:${PRESS_EMAIL_ADDRESS}`;

// errors

export function errorPath(code: number, redirectUrl?: string, errorMessage?: string): string {
  const redirect = redirectUrl ? `&redirectTo=${encodeURIComponent(redirectUrl)}` : "";
  const message = errorMessage ? `&message=${encodeURIComponent(errorMessage)}` : "";

  return `/error?code=${code}${redirect}${message}`;
}

// login

export function loginPath(redirectUrl?: string): string {
  const redirect = redirectUrl ? `?redirectTo=${encodeURIComponent(redirectUrl)}` : "";
  return `/account/login${redirect}`;
}

export function forgotPasswordPath(email?: string) {
  return `/account/reset-password${email ? `?email=${encodeURIComponent(email)}` : ""}`;
}

export function accountSwitcherPath(): string {
  return "/account/dashboard";
}

export function updatePasswordPath(redirectUrl?: string) {
  const redirect = redirectUrl ? `?redirectTo=${encodeURIComponent(redirectUrl)}` : "";
  return `/account/update-password${redirect}`;
}

// builder

function builderPathBase(builderId: string): string {
  return `/builders/${builderId}`;
}

export function builderHomePagePath(builderId: string): string {
  return builderPathBase(builderId);
}

export function builderPlanLibraryPath(builderId: string, notification?: UserNotificationType): string {
  return `/builders/${builderId}/master-plans${notification ? objectToQueryParams(notification) : ""}`;
}

export function builderOptionCatalogPath(builderId: string): string {
  return `/builders/${builderId}/options`;
}

export type AccountPageSection =
  | "profile"
  | "builder_info"
  | "credits"
  | "sales"
  | "brochure"
  | "copy_data"
  | "pricing_visibility"
  | "location_settings";

export function builderAccountPath(builderId: string, section?: AccountPageSection): string {
  const hash = section ? `#${section}` : "";
  return `/builders/${builderId}/account${hash}`;
}

export function builderUsersPath(builderId: string): string {
  return `/builders/${builderId}/users`;
}

export function builderDeveloperPath(builderId: string): string {
  return `/builders/${builderId}/developers`;
}

export function builderAutoRendersPath(builderId: string): string {
  return `/builders/${builderId}/auto-renders`;
}

export function builderIntegrationsPath(builderId: string): string {
  return `/builders/${builderId}/integrations`;
}

// master plans

export function configurePublicPlanPath(
  planType: PublicPlanType,
  builderId: string,
  planId: string,
  lotId?: string,
): string {
  const lotSuffix = lotId ? `&lotId=${lotId}` : "";
  return `${builderPathBase(builderId)}/configure?planType=${planType}&planId=${planId}${lotSuffix}`;
}

export function masterPlanLibraryPath(builderId: string, masterPlanId?: string, tab?: string) {
  let basePath = `/builders/${builderId}/master-plans`;

  if (masterPlanId) {
    basePath += `/${masterPlanId}`;
  }
  return tab ? basePath + `/#${tab}` : basePath;
}

export function locationPlanLibraryPath({
  builderId,
  locationId,
  locationPlanId,
  tab,
}: {
  builderId: string;
  locationId: string;
  locationPlanId: string;
  tab?: string;
}) {
  const basePath = `/builders/${builderId}/locations/${locationId}/location-plans/${locationPlanId}`;
  return tab ? basePath + `/#${tab}` : basePath;
}

export function masterPlanShowroomPath(builderId: string, masterPlanId: string) {
  return `/builders/${builderId}/master-plans/${masterPlanId}/showroom`;
}

export function locationPlanShowroomPath(builderId: string, locationId: string, locationPlanId: string) {
  return `/builders/${builderId}/locations/${locationId}/location-plans/${locationPlanId}/showroom`;
}

export function editShowroomIntroPath(
  planType: PublicPlanType,
  builderId: string,
  planId: string,
  lotId?: string,
): string {
  const lotParam = lotId ? `&lotId=${lotId}` : "";
  const planParam = planType === "community_plans" ? "" : `planType=${planType}`;
  return `/showroom/edit/intro?builderId=${builderId}&${planParam}&planId=${planId}${lotParam}`;
}

export function showroomIntroPath(planType: PublicPlanType, builderId: string, planId: string, lotId?: string): string {
  const lotParam = lotId ? `&lotId=${lotId}` : "";
  const planParam = planType === "community_plans" ? "" : `planType=${planType}`;
  return `/showroom/intro?builderId=${builderId}&${planParam}&planId=${planId}${lotParam}`;
}

export function lotCommunityPlanShowroomPath(
  builderId: string,
  masterPlanId: string,
  communityId: string,
  communityPlanId: string,
  lotId: string,
) {
  return `/builders/${builderId}/master-plans/${masterPlanId}/communities/${communityId}/community-plans/${communityPlanId}/lots/${lotId}/showroom`;
}

export function showroomPath(planType: PublicPlanType, builderId: string, planId: string, lotId?: string): string {
  const lotParam = lotId ? `&lotId=${lotId}` : "";
  const planParam = planType === "community_plans" ? "" : `&planType=${planType}`;
  return `/showroom?builderId=${builderId}${planParam}&planId=${planId}${lotParam}`;
}

export function showroomPlanPath(builderId: string, homePlanId: string): string {
  return `/showroom/plan/${homePlanId}?builderId=${builderId}`;
}

export function showroomPlanOpsPath(builderId: string, homePlanId: string): string {
  return `/showroom/plan/${homePlanId}/ops?builderId=${builderId}`;
}

export function showroomPlanOverviewPath(builderId: string, homePlanId: string): string {
  return `/showroom/plan/${homePlanId}/overview?builderId=${builderId}`;
}

export function showroomStudioPath(studioDocId: string, builderId?: string): string {
  const builderParam = builderId ? `&builderId=${builderId}` : "";
  return `/showroom/studio?studioDocId=${studioDocId}${builderParam}`;
}

// option catalog

export function optionCatalogPath(builderId: string) {
  return `${builderPathBase(builderId)}/options`;
}

// sales and marketing

export function marketingPath(builderId: string, tab?: string) {
  const basePath = `/builders/${builderId}/marketing`;
  return tab ? basePath + `/#${tab}` : basePath;
}

// studio files

export function studioFilesPath(builderId: string) {
  return `${builderPathBase(builderId)}/files`;
}

export function studioLitePath(builderId: string, studioDocId?: string, taskId?: string): string {
  const params = new URLSearchParams();
  if (studioDocId) {
    params.set("id", studioDocId);
  }
  if (taskId) {
    params.set("taskId", taskId);
  }
  const qp = params.toString();
  return `${builderPathBase(builderId)}/guided-flow${qp ? `?${qp}` : ""}`;
}

// locations
export function locationsPath(builderId: string) {
  return `${builderPathBase(builderId)}/locations`;
}

export const LOCATION_TYPE_PARAM = "lt";
export const LOCATION_REDIRECT_PARAM = "rt";
export const LOCATION_PARENT_PARAM = "pl";

export function newLocationPath({
  builderId,
  locationLevel,
  redirectTo,
  parentLocationId,
}: {
  builderId: string;
  locationLevel: LocationLevelType;
  redirectTo: string;
  parentLocationId?: string;
}) {
  const params = new URLSearchParams({
    [LOCATION_TYPE_PARAM]: locationLevel,
    [LOCATION_REDIRECT_PARAM]: redirectTo,
  });
  if (parentLocationId) {
    params.append(LOCATION_PARENT_PARAM, parentLocationId);
  }

  return `${locationsPath(builderId)}/new?${params.toString()}`;
}

export function editLocationPath({
  builderId,
  locationId,
  redirectTo,
}: {
  builderId: string;
  locationId: string;
  redirectTo: string;
}): string {
  const params = new URLSearchParams({ [LOCATION_REDIRECT_PARAM]: redirectTo });
  return `${locationPath(builderId, locationId)}/edit?${params.toString()}`;
}

function locationPath(builderId: string, locationId: string) {
  return `${builderPathBase(builderId)}/locations/${locationId}`;
}

export function locationHomePlansPath(builderId: string, locationId: string) {
  return `${locationPath(builderId, locationId)}/home-plans`;
}

export function locationDefaultPath(
  builderId: string,
  locationId: string,
  locationType: LocationLevelType | undefined,
  disableLots?: boolean,
  descendantsHaveLots?: boolean,
  canViewLots?: boolean,
  canViewChildLocations?: boolean,
  canViewTasks?: boolean,
) {
  if (canViewTasks) {
    return locationTasksPath(builderId, locationId);
  }

  const prefersSubLocations = locationType === "company" || locationType === "region" || locationType === "division";
  if (prefersSubLocations && canViewChildLocations) {
    return locationSubLocationsPath(builderId, locationId);
  }

  // Lots available → lots
  if (canViewLots !== false && (!disableLots || descendantsHaveLots)) {
    return locationLotsPath(builderId, locationId);
  }

  // Sub locations available → sub locations
  if (canViewChildLocations) {
    return locationSubLocationsPath(builderId, locationId);
  }

  // Fallback → plan list for types that prefer sub locations, home plans for the rest
  if (prefersSubLocations) {
    return locationPlanListPath(builderId, locationId);
  }
  return locationHomePlansPath(builderId, locationId);
}

export function locationPlanListPath(builderId: string, locationId: string) {
  return `${locationPath(builderId, locationId)}/location-plans`;
}

export function locationLotsPath(builderId: string, locationId: string) {
  return `${locationPath(builderId, locationId)}/lots`;
}

export function locationLotDetailsPath(builderId: string, locationId: string, lotId: string) {
  return `${locationPath(builderId, locationId)}/lots/${lotId}`;
}

export function locationMapPath(builderId: string, locationId: string) {
  return `${locationPath(builderId, locationId)}/map`;
}

export function locationPublicMapPath(builderId: string, locationId: string) {
  return `${locationPath(builderId, locationId)}/sales-map`;
}

export function locationSubLocationsPath(builderId: string, locationId: string) {
  return `${locationPath(builderId, locationId)}/sub-locations`;
}

export function locationOptionsPath(builderId: string, locationId: string) {
  return `${locationPath(builderId, locationId)}/options`;
}

export function locationTasksPath(builderId: string, locationId: string) {
  return `${locationPath(builderId, locationId)}/tasks`;
}

export function locationWorkbooksPath(builderId: string, locationId: string) {
  return `${locationPath(builderId, locationId)}/workbooks`;
}

export function locationStudioDocsPath(builderId: string, locationId: string) {
  return `${locationPath(builderId, locationId)}/studio-docs`;
}

// home plans

export function homePlanPath(builderId: string, homePlanId: string): string {
  return `${builderPathBase(builderId)}/home-plans/${homePlanId}`;
}

export function homePlanOverviewInternalPath(builderId: string, homePlanId: string): string {
  return `/builders/${builderId}/home-plans/${homePlanId}/overview-internal`;
}

export function homePlanOverviewPublicPath(builderId: string, homePlanId: string): string {
  return `${builderPathBase(builderId)}/home-plans/${homePlanId}/overview`;
}

export function homePlanShowroomPath(builderId: string, homePlanId: string): string {
  return `${builderPathBase(builderId)}/home-plans/${homePlanId}/showroom`;
}

export function homePlanOpsViewPath(builderId: string, homePlanId: string): string {
  return `${builderPathBase(builderId)}/home-plans/${homePlanId}/ops-view`;
}

// lots

export function lotCommunityPlanLibraryPath(
  builderId: string,
  masterPlanId: string,
  communityId: string,
  communityPlanId: string,
  lotId: string,
  tab?: string,
) {
  const basePath = `/builders/${builderId}/master-plans/${masterPlanId}/communities/${communityId}/community-plans/${communityPlanId}/lots/${lotId}`;
  return tab ? basePath + `/#${tab}` : basePath;
}

// editor

export const STUDIO_BASE_PATHS = {
  studio: "/studio",
  studioShowroom: "/studio-showroom",
} as const;

export function studioPath(studioDocId?: string, builderId?: string, view?: string): string {
  const qp = idWithOptionalBuilderIdParams(studioDocId, builderId, view ? { view } : undefined);
  return `${STUDIO_BASE_PATHS.studio}${qp}`;
}

export function studioShowroomPath(studioDocId: string, builderId?: string) {
  const qp = idWithOptionalBuilderIdParams(studioDocId, builderId);
  return `${STUDIO_BASE_PATHS.studioShowroom}${qp}`;
}

// estimate

export const PROCURE_BASE_PATHS = {
  measures: "",
  integrations: "/integrations",
  resources: "/integrations/resources",
  costcodes: "/integrations/costcodes",
  customfields: "/integrations/customfields",
} as const;

export type ProcurePath = keyof typeof PROCURE_BASE_PATHS;

export type ProcureQueryParams = {
  builderId?: string;
  workbookId?: string;
  studioDocId?: string;
  masterPlanId?: string;
  communityPlanId?: string;
  homePlanId?: string;
  versionId?: string;
  path?: ProcurePath;
};

export function procurePath({
  builderId,
  workbookId,
  studioDocId,
  masterPlanId,
  communityPlanId,
  homePlanId,
  versionId,
  path,
}: ProcureQueryParams): string {
  const procureBaseURL = `/builders/${builderId}/estimate${path ? PROCURE_BASE_PATHS[path] : ""}`;
  const urlParams = new URLSearchParams();

  if (workbookId) {
    urlParams.set("workbookId", workbookId);
  }

  // prioritize plans that are further down the hierarchy
  if (homePlanId) {
    urlParams.set("homePlanId", homePlanId);
  } else if (communityPlanId) {
    urlParams.set("locationPlanId", communityPlanId);
  } else if (masterPlanId) {
    urlParams.set("masterPlanId", masterPlanId);
  } else if (studioDocId) {
    urlParams.set("studioDocId", studioDocId);
  }

  if (versionId) {
    urlParams.set("versionId", versionId);
  }

  if (urlParams.toString() === "") {
    return procureBaseURL;
  }

  return `${procureBaseURL}?${urlParams.toString()}`;
}

// estimation tasks

export function tasksPath(builderId: string): string {
  return `${builderPathBase(builderId)}/tasks`;
}

export function taskPath(builderId: string, taskId: string): string {
  return `${tasksPath(builderId)}/${taskId}`;
}

export function taskOptionsPath(builderId: string, taskId: string): string {
  return `${taskPath(builderId, taskId)}/options`;
}

// integrations

export function integrationJobsPath(builderId: string, integrationId?: string, jobType?: JobType, homePlanId?: string) {
  const params = new URLSearchParams();
  if (integrationId) {
    params.set("integrationId", integrationId);
  }
  if (jobType) {
    params.set("type", jobType);
  }
  if (homePlanId) {
    params.set("homePlanId", homePlanId);
  }
  const qp = params.toString();
  return `/builders/${builderId}/integrations${qp ? `?${qp}` : ""}#jobs`;
}

export function integrationConnectorAgentPath(builderId: string, integrationId?: string) {
  const params = new URLSearchParams();
  if (integrationId) {
    params.set("integrationId", integrationId);
  }
  const qp = params.toString();
  return `/builders/${builderId}/integrations${qp ? `?${qp}` : ""}#connector_agent`;
}

// navigation helpers

export function returnToLastPageOrDashboard(builderId?: string) {
  if (window.history.length > 1) {
    window.history.back();
  } else if (builderId) {
    window.location.href = builderHomePagePath(builderId);
  }
}

// hooks

/*
 * Extracts route params and search params from TanStack Router and converts them from snake_case to camelCase.
 * i.e. "/builders/1234/communities/5678" would extract "builder_id" => "builderId" & "community_id" => "communityId"
 *
 * The "isReady" property is always true (retained for backwards compatibility with consumers).
 * The location pathname is passed to "useMemo" to ensure that the params returned refresh during navigation.
 */
function useRouterParams<T extends { isReady: boolean }>(): T {
  const params = useParams({ strict: false }) as Record<string, string>;
  const location = useLocation();
  // During startTransition, the resolved location (last committed route) lags
  // behind the current location (where the router is navigating to).  When they
  // differ, route params from useParams/useLocation are unreliable.
  const isReady = useRouterState({
    select: (s) => s.resolvedLocation?.pathname === s.location?.pathname,
  });

  return useMemo(() => {
    const search = (location.search ?? {}) as Record<string, string>;
    const query = { ...params, ...search };
    return Object.keys(query).reduce<T>(
      (prev, curr) => ({ ...prev, [snakeCaseToCamelCase(curr)]: query[curr], isReady }),
      { isReady } as T,
    );
  }, [params, location.search, isReady]);
}

// Any of the query params referenced in the application
export interface AppRouterParams {
  isReady: boolean;

  id?: string;
  builderId?: string;
  lotId?: string;
  planId?: string;
  renderSource?: string;
  step?: string;
  homePlanId?: string;
  planType?: PublicPlanType;
  masterPlanId?: string;
  studioDocId?: string;
  versionId?: string;
  workbookId?: string;
  email?: string;
  redirectTo?: string;
  feature?: FeatureName;
  group?: string;
  inviteId?: string;
  isRedirect?: string;
  code?: string;
  message?: string;
  showroomPlanViewerOnly?: string;
  walkthroughId?: string;
  initialPropId?: string;
  initialPropTagName?: string;
  initialPropBuilder?: string;
  integrationId?: string;
  locationId?: string;
  locationPlanId?: string;
  builderName?: string;
  sceneId?: string;
  itemId?: string;
  taskId?: string;
}

export function useAppRouterParams(): AppRouterParams {
  return useRouterParams<AppRouterParams>();
}

// Re-export useAppNavigate from ui for convenience
export { useAppNavigate } from "ui/hooks/useAppNavigate";

export function openNewTab(url: string, options: { noopener?: boolean; noreferrer?: boolean } = {}) {
  if (typeof window !== "undefined") {
    const { noopener = true, noreferrer = true } = options;
    const attributes = [noopener ? "noopener" : "", noreferrer ? "noreferrer" : ""].filter(Boolean).join(",");
    window.open(url, "_blank", attributes);
  }
}

export function getQueryParams(): URLSearchParams | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return new URLSearchParams(window.location.search);
}

export function getQueryParam(param: string): string | undefined {
  const url = getQueryParams();
  if (!url) {
    return undefined;
  }
  return url.get(param) ?? undefined;
}
